import { useCallback, useEffect, useRef, useState } from 'react';
import { InvoiceModel, PaymentOrigin } from '@frontend/api-invoices';
import { PersonsV3 } from '@frontend/api-person';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { paymentsSentry } from '@frontend/payments-hooks';
import { ITerminalStrategyError } from '@frontend/payments-terminal-controller';
import { useCreatePaymentIntentSync, useInvoicePerson } from '../../../hooks';

export type UseCreatePaymentIntentProps = {
  person: ReturnType<typeof useInvoicePerson>['person'];
  invoice: InvoiceModel | undefined;
  setupFutureUsage?: SetupFutureUsage | null;
  onGoBack?: () => void;
  onRefetchInvoice: () => Promise<InvoiceModel | undefined>;
};

export const useCreateTerminalPaymentIntent = ({
  person,
  invoice,
  setupFutureUsage = SetupFutureUsage.offSession,
  onGoBack,
  onRefetchInvoice,
}: UseCreatePaymentIntentProps) => {
  const [paymentIntent, setPaymentIntent] = useState<Awaited<ReturnType<typeof createPaymentIntent>>>();
  const [error, setError] = useState<ITerminalStrategyError>();

  const personEmail = person ? PersonsV3.PersonHelpers.getEmailContactType(person) : '';

  const { createPaymentIntent } = useCreatePaymentIntentSync({
    amount: invoice?.billedAmount ?? 0,
    email: personEmail,
    personId: person?.personId,
    pmId: person?.personPmid,
    setupFutureUsage: setupFutureUsage || undefined,
    locationId: invoice?.locationId,
    origin: PaymentOrigin.Terminal,
    invoiceId: invoice?.id,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const createOnce = useRef(false);

  const tryCreatePaymentIntent = useCallback(async () => {
    let intent: typeof paymentIntent | undefined;
    let paymentIntentError: ITerminalStrategyError | undefined;
    if (!createOnce.current) {
      createOnce.current = true;
      try {
        intent = await createPaymentIntent();
        setPaymentIntent(intent);
      } catch (error) {
        if (error instanceof HttpError || error instanceof Error) {
          paymentIntentError = { action: 'initialize', message: error.message };
          setError(paymentIntentError);
          paymentsSentry.error('Failed to create terminal payment intent', 'TerminalPaymentIntent', error.message);
        }
      }
    }
    return { intent, paymentIntentError };
  }, [createPaymentIntent]);

  useEffect(() => {
    tryCreatePaymentIntent();
  }, [createPaymentIntent, paymentIntent?.paymentIntent?.clientSecret]);

  const retryCreatePaymentIntent = useCallback(async () => {
    createOnce.current = false;

    const { intent, paymentIntentError } = await tryCreatePaymentIntent();

    if (paymentIntentError || !intent?.paymentIntent || !intent.paymentId) {
      throw paymentIntentError ?? new Error('Failed to create payment intent');
    }

    return { paymentIntent: intent.paymentIntent, paymentId: intent.paymentId };
  }, [tryCreatePaymentIntent]);

  return { paymentIntent, error, retryCreatePaymentIntent };
};
